import { useEffect, useRef, useState } from 'react'
import { Alert, Dropdown, Space, Typography, type MenuProps } from 'antd'
import { DownOutlined, DownloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  EXPORT_FORMATS,
  EXPORT_MAX_ROWS,
  UNAVAILABLE_FORMATS,
  translateExportError,
  type ExportFormat,
} from '@/shared/lib/serverExport'
import { formatBytes, formatNumber } from '@/shared/lib/format'

export interface ServerExportButtonProps {
  /** Formatni olib faylni yuklab oladigan amal */
  run: (format: ExportFormat, onProgress: (loaded: number) => void) => Promise<void>
  /** Tugma matni. Berilmasa — «Yuklab olish» */
  label?: string
  /** Yuklab olinadigan narsa nima ekani — menyu sarlavhasida ko'rinadi */
  title?: string
  size?: 'small' | 'middle' | 'large'
  disabled?: boolean
  /**
   * Shu filtr bilan kutilayotgan qator soni — TAXMINIY.
   *
   * ⚠️ Aniq son emasligi ATAYLAB ta'kidlanadi ("~"): u ekrandagi
   * ro'yxatning jami sonidan olinadi, server esa faylni boshqa oyna
   * yoki boshqa doira bilan yig'ishi mumkin. Taxminni aniq son qilib
   * ko'rsatish — panel bilmagan narsani bilgandek ko'rsatish bo'lardi.
   */
  estimatedRows?: number | null
}

/**
 * Bitta bosishda keladigan format.
 *
 * ⚠️ Aynan CSV va bu tanlov emas, MAJBURIYAT: davomat eksporti (tugmasi
 * ishlaydigan yagona eksport edi) ham CSV beradi va operator undan
 * shuni kutadi. XLSX ni sukut qilish katta hisobotda bir bosishni
 * daqiqalarga cho'zardi.
 */
const DEFAULT_FORMAT: ExportFormat = 'csv'

/**
 * SERVERDAGI eksport tugmasi — format tanlash, chegara, jarayon va xato.
 *
 * <h2>⚠️ BITTA BOSISH FAYL BERADI — bu tugmaning ASOSIY shartnomasi</h2>
 *
 * <p>2026-08-14 da tashqi tester «Hisobotlar sahifasidagi ikkala
 * Download tugmasi 10 soniyada fayl bermadi» deb yozdi va aybdor
 * BACKEND emas edi: bu komponent sof {@link Dropdown} edi, ya'ni
 * tugmani bosish faqat MENYU ochardi. Fayl faqat menyudan format
 * tanlangandan keyin kelardi — ikki bosish.
 *
 * <p>Farqning dalili o'sha kunning o'zida bor edi: davomat hisobotining
 * CSV eksporti ISHLAGAN (1414 baytlik fayl olingan). Uning tugmasi
 * oddiy {@code <Button onClick=…>} — bitta bosish, darhol yuklab olish
 * ({@code attendanceApi.downloadAttendanceExport}). Ya'ni umumiy
 * eksport dvigateli, huquq va marshrut sog' edi; nosoz bo'lgani —
 * aynan shu tugmaning o'zi.
 *
 * <p>Endi u <b>bo'lingan tugma</b> ({@code Dropdown.Button}):
 * <ul>
 *   <li>chap yarmi — {@link DEFAULT_FORMAT} bilan DARHOL yuklab oladi;</li>
 *   <li>o'ng yarmi (strelka) — format tanlash menyusi, ilgarigidek.</li>
 * </ul>
 *
 * <p>⚠️ Menyu OLIB TASHLANMADI: XLSX va JSON backendda bor va ular
 * panelga aynan shu menyu orqali keldi (pastdagi 1-band). Muammo
 * tanlovda emas, tanlovni MAJBURIY qilganda edi.
 *
 * <h2>To'rt narsa, va har birining o'lchangan sababi bor</h2>
 *
 * <b>1. Format tanlanadi.</b> Backend dvigateli CSV, XLSX va JSON ni
 * beradi. Panel esa faqat CSV so'rardi, ya'ni Excel fayli mavjud bo'la
 * turib operatorga yetib bormasdi.
 *
 * <b>2. PDF menyuda KO'RINADI, lekin o'chirilgan holda va SABABI bilan.</b>
 * Uni ro'yxatdan butunlay olib tashlash "unutilgan" degan taassurot
 * qoldirardi va har operator uni alohida so'rardi. Sabab
 * `serverExport.ts` da yozilgan: litsenziya va arab yozuvining
 * shakllanmasligi.
 *
 * <b>3. Chegara OLDINDAN aytiladi.</b> Server 200 000 qatordan keyin
 * faylni kesadi va ogohlantirishni fayl ICHIGA yozadi — ya'ni operator
 * buni uzoq kutgandan KEYIN bilardi. Taxminiy son chegaradan oshsa,
 * ogohlantirish tugmaning yonida turadi.
 *
 * <b>4. Ikki marta bosib bo'lmaydi.</b> Katta eksport daqiqalar davom
 * etadi va "hech narsa bo'lmadi" deb o'ylagan operator qayta bosadi —
 * server esa butun ishni ikkinchi marta bajaradi. Tugma band holatda
 * qulflanadi va yuklangan hajm ko'rinib turadi.
 *
 * ⚠️ Xato TUGMANING YONIDA ko'rsatiladi, tooltip ichida emas: tooltip
 * faqat sichqoncha ustiga kelganda ochiladi, ya'ni xabar amalda
 * ko'rinmasdi (`guards.mjs` §1 dagi bilan bir xil oila).
 */
export function ServerExportButton({
  run,
  label,
  title,
  size = 'small',
  disabled = false,
  estimatedRows = null,
}: ServerExportButtonProps) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(0)
  const [failed, setFailed] = useState<string | null>(null)

  /**
   * ⚠️ `useState` YETARLI EMAS. React holatni paket bilan yangilaydi,
   * ya'ni ikkita tez ketma-ket bosishda ikkinchisi hali `busy === false`
   * ni ko'radi va ikkinchi so'rov ketadi. `ref` darhol o'zgaradi.
   */
  const running = useRef(false)
  /** Komponent yopilgandan keyin holat yangilanmasin */
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const start = (format: ExportFormat) => {
    if (running.current) return
    running.current = true
    setBusy(true)
    setLoaded(0)
    setFailed(null)
    run(format, (bytes) => {
      if (alive.current) setLoaded(bytes)
    })
      .catch((error: unknown) => {
        if (alive.current) setFailed(translateExportError(error, t))
      })
      .finally(() => {
        running.current = false
        if (alive.current) {
          setBusy(false)
          setLoaded(0)
        }
      })
  }

  const items: MenuProps['items'] = [
    ...(title === undefined ? [] : [{ key: 'title', type: 'group' as const, label: title }]),
    ...EXPORT_FORMATS.map((format) => ({
      key: format,
      disabled: busy,
      label: t(`export.format.${format}`),
    })),
    { key: 'divider', type: 'divider' as const },
    ...UNAVAILABLE_FORMATS.map((format) => ({
      key: format,
      disabled: true,
      label: (
        <Space direction="vertical" size={0}>
          <span>{t(`export.format.${format}`)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('export.pdfMenuHint')}
          </Typography.Text>
        </Space>
      ),
    })),
  ]

  const overLimit = estimatedRows !== null && estimatedRows > EXPORT_MAX_ROWS

  return (
    <Space direction="vertical" size={4} style={{ maxWidth: 360 }}>
      {/*
        ⚠️ `Dropdown.Button` — `Dropdown` EMAS. Farq bitta bosishda:
        `onClick` chap yarmiga tegishli va u faylni DARHOL boshlaydi.
        Sof `Dropdown` da bosish faqat menyu ochardi va tester buni
        "fayl kelmadi" deb o'lchadi (klass javadoc'i).
      */}
      <Dropdown.Button
        size={size}
        trigger={['click']}
        loading={busy}
        disabled={disabled}
        icon={<DownOutlined />}
        onClick={() => start(DEFAULT_FORMAT)}
        menu={{
          items,
          onClick: ({ key }) => {
            const format = EXPORT_FORMATS.find((f) => f === key)
            if (format) start(format)
          },
        }}
      >
        <DownloadOutlined /> {label ?? t('export.download')}
      </Dropdown.Button>

      {busy ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('export.inProgress', { size: formatBytes(loaded) })}
        </Typography.Text>
      ) : null}

      {estimatedRows !== null && !busy ? (
        <Typography.Text type={overLimit ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
          {overLimit
            ? t('export.overLimit', {
                rows: formatNumber(estimatedRows),
                limit: formatNumber(EXPORT_MAX_ROWS),
              })
            : t('export.estimatedRows', { rows: formatNumber(estimatedRows) })}
        </Typography.Text>
      ) : null}

      {failed !== null ? (
        <Alert
          type="error"
          showIcon
          closable
          message={failed}
          onClose={() => setFailed(null)}
          style={{ fontSize: 12 }}
        />
      ) : null}
    </Space>
  )
}
